(() => {
  const root = document.querySelector('[data-elementra-radio]');
  const playlistNode = document.querySelector('[data-elementra-radio-playlist]');
  if (!root || !playlistNode || root.dataset.initialized === 'true') return;

  root.dataset.initialized = 'true';
  const playlist = JSON.parse(playlistNode.textContent);
  const audio = root.querySelector('[data-elementra-radio-audio]');
  const panel = root.querySelector('.elementra-radio__panel');
  const reopen = root.querySelector('[data-elementra-radio-open]');
  const playButton = root.querySelector('[data-elementra-radio-play]');
  const playIcon = root.querySelector('[data-elementra-radio-play-icon]');
  const title = root.querySelector('[data-elementra-radio-title]');
  const count = root.querySelector('[data-elementra-radio-count]');
  const seek = root.querySelector('[data-elementra-radio-seek]');
  const progress = root.querySelector('[data-elementra-radio-progress]');
  const volume = root.querySelector('[data-elementra-radio-volume]');
  const storageKey = 'elementraRadioState';
  const playbackSessionKey = 'elementraRadioPlaybackAuthorized';
  const defaults = {
    track: 0,
    open: true,
    volume: 0.55,
    position: 0,
    initiated: false,
    wasPlaying: false,
    userPaused: false
  };

  let state = defaults;
  try {
    state = { ...defaults, ...JSON.parse(localStorage.getItem(storageKey) || '{}') };
  } catch (error) {
    console.warn('Elementra Radio: saved state could not be read.', error);
  }

  state.track = Number.isInteger(Number(state.track)) && Number(state.track) >= 0 && Number(state.track) < playlist.length
    ? Number(state.track)
    : 0;
  state.volume = Math.min(Math.max(Number(state.volume) || defaults.volume, 0), 1);
  state.position = Math.max(Number(state.position) || 0, 0);
  audio.volume = state.volume;
  volume.value = String(state.volume);

  const save = () => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(state));
    } catch (error) {
      console.warn('Elementra Radio: state could not be saved.', error);
    }
  };

  const syncOpenState = () => {
    panel.hidden = !state.open;
    reopen.hidden = state.open;
  };

  const syncPlayState = () => {
    const playing = !audio.paused;
    playButton.setAttribute('aria-label', playing ? 'Pause' : 'Play');
    playIcon.textContent = playing ? '\u275A\u275A' : '\u25B6';
  };

  const syncProgress = () => {
    const duration = Number.isFinite(audio.duration) && audio.duration > 0 ? audio.duration : 0;
    const currentTime = Number.isFinite(audio.currentTime) ? audio.currentTime : 0;
    const percentage = duration ? Math.min(Math.max((currentTime / duration) * 100, 0), 100) : 0;
    progress.style.width = `${percentage}%`;
    seek.setAttribute('aria-valuenow', String(Math.round(percentage)));
  };

  const startPlayback = ({ markInitiated = true } = {}) => {
    const playAttempt = audio.play();
    if (!playAttempt || typeof playAttempt.then !== 'function') return Promise.resolve();

    return playAttempt.then(() => {
      if (markInitiated) state.initiated = true;
      state.userPaused = false;
      state.wasPlaying = true;
      save();
    }).catch(() => {
      state.wasPlaying = false;
      save();
      syncPlayState();
    });
  };

  const loadTrack = ({ restorePosition = false, resume = false } = {}) => {
    const track = playlist[state.track];
    audio.src = track.src;
    title.textContent = track.title;
    count.textContent = `${String(state.track + 1).padStart(2, '0')} / ${String(playlist.length).padStart(2, '0')}`;
    syncProgress();

    if (restorePosition && state.position) {
      audio.addEventListener('loadedmetadata', () => {
        if (Number.isFinite(audio.duration)) audio.currentTime = Math.min(state.position, Math.max(audio.duration - 0.25, 0));
      }, { once: true });
    }

    if (resume && state.initiated && !state.userPaused) {
      startPlayback();
    }
  };

  const move = (direction, resume = !audio.paused) => {
    state.track = (state.track + direction + playlist.length) % playlist.length;
    state.position = 0;
    state.wasPlaying = resume;
    save();
    loadTrack({ resume });
  };

  root.querySelector('[data-elementra-radio-previous]').addEventListener('click', () => move(-1));
  root.querySelector('[data-elementra-radio-next]').addEventListener('click', () => move(1));

  playButton.addEventListener('click', () => {
    if (audio.paused) {
      state.initiated = true;
      state.userPaused = false;
      state.wasPlaying = true;
      try {
        sessionStorage.setItem(playbackSessionKey, 'true');
      } catch (error) {
        // Playback still works when session storage is unavailable.
      }
      startPlayback();
    } else {
      state.userPaused = true;
      audio.pause();
      state.wasPlaying = false;
    }
    save();
  });

  root.querySelector('[data-elementra-radio-close]').addEventListener('click', () => {
    state.open = false;
    syncOpenState();
    save();
  });

  reopen.addEventListener('click', () => {
    state.open = true;
    syncOpenState();
    save();
  });

  volume.addEventListener('input', () => {
    state.volume = Number(volume.value);
    audio.volume = state.volume;
    save();
  });

  let isSeeking = false;

  const seekToPointer = (event) => {
    if (!Number.isFinite(audio.duration) || audio.duration <= 0) return;
    const bounds = seek.getBoundingClientRect();
    if (!bounds.width) return;
    const ratio = Math.min(Math.max((event.clientX - bounds.left) / bounds.width, 0), 1);
    audio.currentTime = ratio * audio.duration;
    state.position = audio.currentTime;
    syncProgress();
  };

  seek.addEventListener('pointerdown', (event) => {
    event.preventDefault();
    isSeeking = true;
    seek.setPointerCapture?.(event.pointerId);
    seekToPointer(event);
  });

  seek.addEventListener('pointermove', (event) => {
    if (!isSeeking) return;
    event.preventDefault();
    seekToPointer(event);
  });

  const finishSeeking = (event) => {
    if (!isSeeking) return;
    seekToPointer(event);
    isSeeking = false;
    seek.releasePointerCapture?.(event.pointerId);
    save();
  };

  seek.addEventListener('pointerup', finishSeeking);
  seek.addEventListener('pointercancel', () => {
    isSeeking = false;
  });

  seek.addEventListener('keydown', (event) => {
    if (!Number.isFinite(audio.duration) || audio.duration <= 0) return;
    let nextTime = audio.currentTime;
    if (event.key === 'ArrowLeft') nextTime -= 5;
    else if (event.key === 'ArrowRight') nextTime += 5;
    else if (event.key === 'Home') nextTime = 0;
    else if (event.key === 'End') nextTime = audio.duration;
    else return;

    event.preventDefault();
    audio.currentTime = Math.min(Math.max(nextTime, 0), audio.duration);
    state.position = audio.currentTime;
    syncProgress();
    save();
  });

  audio.addEventListener('play', syncPlayState);
  audio.addEventListener('pause', syncPlayState);
  audio.addEventListener('ended', () => {
    state.userPaused = false;
    move(1, true);
  });
  audio.addEventListener('error', () => console.warn(`Elementra Radio: failed to load ${playlist[state.track].title}. Use Next to continue.`));
  audio.addEventListener('timeupdate', () => {
    if (Number.isFinite(audio.duration) && audio.duration > 0) {
      syncProgress();
    }
  });

  window.addEventListener('pagehide', () => {
    state.position = Number.isFinite(audio.currentTime) ? audio.currentTime : 0;
    state.wasPlaying = state.initiated && !audio.paused;
    save();
  });

  let playbackAuthorizedThisSession = false;
  try {
    playbackAuthorizedThisSession = sessionStorage.getItem(playbackSessionKey) === 'true';
  } catch (error) {
    playbackAuthorizedThisSession = false;
  }

  const shouldResume = playbackAuthorizedThisSession && state.wasPlaying && !state.userPaused;
  if (!playbackAuthorizedThisSession) {
    state.track = 0;
    state.position = 0;
    state.initiated = false;
    state.wasPlaying = false;
  }

  syncOpenState();
  loadTrack({ restorePosition: true, resume: shouldResume });
  syncPlayState();
  root.hidden = false;
})();
